import {Form, InputNumber} from 'antd';
import styles from './mobileSetting.module.scss';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useMutation} from '@apollo/client';
import {
  GET_APP_CONFIGURATION,
  UPDATE_APP_CONFIGURATION,
} from 'graphql/queries/mobileSettings';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {toast} from 'react-toastify';
import {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';

/*
 * Bounds for both sliders. The "hard" caps reject the input outright (they
 * defend against typos like an extra zero); the "soft" caps still accept
 * the value but route through a louder confirm-modal copy so the operator
 * has to acknowledge they're outside the usual range.
 *
 * — Max active tickets per user (`maxActiveTickets`)
 *   default 1; raising it lets a single user juggle parallel threads. Per
 *   docs/admin/ai-support-config.md: 1 is the sane default; ≤10 is
 *   defensible; double-digit is almost always a misconfig.
 *
 * — Max user message characters (`maxUserMessageCharacters`)
 *   No published spec yet — picked guardrails from common chat-app norms.
 *   100 chars is the lowest value that still allows a usable support
 *   question; 5000 is a generous power-user message; anything beyond
 *   20000 is almost certainly a typo or pasted log dump.
 */
const MIN_TICKETS = 1;
const SOFT_MAX_TICKETS = 10;
const HARD_MAX_TICKETS = 50;
const DEFAULT_TICKETS = 1;

const MIN_CHARS = 100;
const SOFT_MAX_CHARS = 5000;
const HARD_MAX_CHARS = 20000;

// — Max active anonymous tickets per contactEmail (`maxActiveAnonymousTickets`)
//   default 1; at the cap a repeat createTicket for the same email resumes the
//   most-recent active anonymous ticket instead of opening a new one. Anonymous
//   is more abuse-prone than authenticated, so the soft cap sits lower (5).
const MIN_ANON_TICKETS = 1;
const SOFT_MAX_ANON_TICKETS = 5;
const HARD_MAX_ANON_TICKETS = 50;
const DEFAULT_ANON_TICKETS = 1;

// — Idle-sweep threshold in minutes (`abandonAfterMinutes`)
//   default 1440 (24h); idle anonymous AND authenticated tickets past this are
//   swept to ABANDONED. Floor at 30m so a brief step-away can't abandon a live
//   chat; soft-warn past 7 days; hard cap at 30 days.
const MIN_ABANDON_MINUTES = 30;
const SOFT_MAX_ABANDON_MINUTES = 10080;
const HARD_MAX_ABANDON_MINUTES = 43200;
const DEFAULT_ABANDON_MINUTES = 1440;

const normalizeInt = (raw, fallback) => {
  if (typeof raw !== 'number') return fallback;
  if (!Number.isFinite(raw) || raw < 1) return fallback;
  return Math.floor(raw);
};

export const AiSupportSettings = ({data, refetch}) => {
  const [form] = Form.useForm();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.PlatformSettings),
  );

  const [updateAppConfig, {loading}] = useMutation(UPDATE_APP_CONFIGURATION, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  // Sassandra falls back to its server-side default when the field is
  // missing or invalid. We mirror that for `maxActiveTickets` (default 1)
  // and leave `maxUserMessageCharacters` blank when unset so the operator
  // sees "use server default" rather than a fabricated number.
  const storedTickets = normalizeInt(data?.maxActiveTickets, DEFAULT_TICKETS);
  const storedChars =
    typeof data?.maxUserMessageCharacters === 'number' &&
    data.maxUserMessageCharacters >= 1
      ? Math.floor(data.maxUserMessageCharacters)
      : null;
  const storedAnonTickets = normalizeInt(
    data?.maxActiveAnonymousTickets,
    DEFAULT_ANON_TICKETS,
  );
  const storedAbandon = normalizeInt(
    data?.abandonAfterMinutes,
    DEFAULT_ABANDON_MINUTES,
  );

  useEffect(() => {
    form.setFieldsValue({
      maxActiveTickets: storedTickets,
      maxUserMessageCharacters: storedChars,
      maxActiveAnonymousTickets: storedAnonTickets,
      abandonAfterMinutes: storedAbandon,
    });
  }, [storedTickets, storedChars, storedAnonTickets, storedAbandon, form]);

  const validateInt = (value, {min, max, label}) => {
    if (value === null || value === undefined || value === '') {
      // Empty is allowed only where we explicitly say so per field.
      return Promise.resolve();
    }
    const num = Number(value);
    if (!Number.isInteger(num)) {
      return Promise.reject(new Error('Must be a whole number (no decimals)'));
    }
    if (num < min) {
      return Promise.reject(new Error(`${label} must be at least ${min}`));
    }
    if (num > max) {
      return Promise.reject(
        new Error(
          `${label} must be ${max} or less — values past this are almost certainly a typo`,
        ),
      );
    }
    return Promise.resolve();
  };

  const handleFinish = values => {
    const tickets = Number(values.maxActiveTickets);
    const charsRaw = values.maxUserMessageCharacters;
    const charsProvided =
      charsRaw !== null && charsRaw !== undefined && charsRaw !== '';
    const chars = charsProvided ? Number(charsRaw) : null;
    const anonTickets = Number(values.maxActiveAnonymousTickets);
    const abandonMinutes = Number(values.abandonAfterMinutes);

    if (
      !Number.isInteger(tickets) ||
      tickets < MIN_TICKETS ||
      tickets > HARD_MAX_TICKETS
    ) {
      toast.error(
        `Max active tickets must be a whole number between ${MIN_TICKETS} and ${HARD_MAX_TICKETS}.`,
      );
      return;
    }
    if (
      charsProvided &&
      (!Number.isInteger(chars) || chars < MIN_CHARS || chars > HARD_MAX_CHARS)
    ) {
      toast.error(
        `Max message characters must be a whole number between ${MIN_CHARS} and ${HARD_MAX_CHARS}.`,
      );
      return;
    }
    if (
      !Number.isInteger(anonTickets) ||
      anonTickets < MIN_ANON_TICKETS ||
      anonTickets > HARD_MAX_ANON_TICKETS
    ) {
      toast.error(
        `Max active anonymous tickets must be a whole number between ${MIN_ANON_TICKETS} and ${HARD_MAX_ANON_TICKETS}.`,
      );
      return;
    }
    if (
      !Number.isInteger(abandonMinutes) ||
      abandonMinutes < MIN_ABANDON_MINUTES ||
      abandonMinutes > HARD_MAX_ABANDON_MINUTES
    ) {
      toast.error(
        `Abandon-after minutes must be a whole number between ${MIN_ABANDON_MINUTES} and ${HARD_MAX_ABANDON_MINUTES}.`,
      );
      return;
    }
    setPendingValues({
      tickets,
      chars: charsProvided ? chars : null,
      anonTickets,
      abandonMinutes,
    });
    setConfirmModalOpen(true);
  };

  const persist = () => {
    if (!pendingValues) return;
    // Build only the fields the operator actually changed — the GraphQL
    // resolver does a `$set` on each provided field, so omitted fields stay
    // untouched. That keeps room for future siblings under `aiSupport`
    // (escalationThreshold, modelKey, disabled, languages) without us
    // having to read-modify-write the whole object.
    const aiSupportPatch = {};
    if (pendingValues.tickets !== storedTickets) {
      aiSupportPatch.maxActiveTickets = pendingValues.tickets;
    }
    if ((pendingValues.chars ?? null) !== (storedChars ?? null)) {
      aiSupportPatch.maxUserMessageCharacters = pendingValues.chars;
    }
    if (pendingValues.anonTickets !== storedAnonTickets) {
      aiSupportPatch.maxActiveAnonymousTickets = pendingValues.anonTickets;
    }
    if (pendingValues.abandonMinutes !== storedAbandon) {
      aiSupportPatch.abandonAfterMinutes = pendingValues.abandonMinutes;
    }
    if (Object.keys(aiSupportPatch).length === 0) {
      toast.info('Nothing to update — values match the current settings.');
      setConfirmModalOpen(false);
      setPendingValues(null);
      return;
    }
    updateAppConfig({
      variables: {data: {aiSupport: aiSupportPatch}},
      refetchQueries: [{query: GET_APP_CONFIGURATION}],
    })
      .then(({data: response}) => {
        const result = response?.updateAppConfig;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
          return;
        }
        toast.success(
          'Customer Support settings saved. Changes will take effect within ~60 seconds.',
        );
        setConfirmModalOpen(false);
        setPendingValues(null);
        refetch?.();
      })
      .catch(error => toast.error(error?.message || 'Update failed'));
  };

  const exceedsTicketsSoftCap =
    pendingValues && pendingValues.tickets > SOFT_MAX_TICKETS;
  const exceedsCharsSoftCap =
    pendingValues &&
    pendingValues.chars !== null &&
    pendingValues.chars > SOFT_MAX_CHARS;
  const exceedsAnonTicketsSoftCap =
    pendingValues && pendingValues.anonTickets > SOFT_MAX_ANON_TICKETS;
  const exceedsAbandonSoftCap =
    pendingValues && pendingValues.abandonMinutes > SOFT_MAX_ABANDON_MINUTES;
  const anySoftCapBreach =
    exceedsTicketsSoftCap ||
    exceedsCharsSoftCap ||
    exceedsAnonTicketsSoftCap ||
    exceedsAbandonSoftCap;

  return (
    <div className={styles.body}>
      <div className={styles.top}>
        <h1>Customer Support</h1>
        <p>
          Controls how the AI helpdesk handles concurrent support tickets for a
          single user. These settings affect every user on the platform.
        </p>
      </div>
      <div className={styles.salesAgentCard}>
        <Form
          form={form}
          onFinish={handleFinish}
          layout="vertical"
          className={styles.salesAgentForm}
          initialValues={{
            maxActiveTickets: storedTickets,
            maxUserMessageCharacters: storedChars,
            maxActiveAnonymousTickets: storedAnonTickets,
            abandonAfterMinutes: storedAbandon,
          }}>
          <Form.Item
            name="maxActiveTickets"
            label="Max active tickets per user"
            extra="When a user is at this limit, sassandra resumes their most-recent open ticket instead of opening a new one. Default: 1."
            rules={[
              {required: true, message: 'Please enter a value'},
              {
                validator: (_, value) =>
                  validateInt(value, {
                    min: MIN_TICKETS,
                    max: HARD_MAX_TICKETS,
                    label: 'Max active tickets',
                  }),
              },
            ]}>
            <InputNumber
              min={MIN_TICKETS}
              max={HARD_MAX_TICKETS}
              step={1}
              precision={0}
              placeholder="1"
              className={styles.salesAgentInput}
              parser={value => (value === '' ? '' : parseInt(value, 10))}
            />
          </Form.Item>

          <Form.Item
            name="maxUserMessageCharacters"
            label="Max characters per user message"
            extra={`The longest single chat message a user can send (between ${MIN_CHARS} and ${HARD_MAX_CHARS} characters). Leave empty to use sassandra's server-side default.`}
            rules={[
              {
                validator: (_, value) =>
                  validateInt(value, {
                    min: MIN_CHARS,
                    max: HARD_MAX_CHARS,
                    label: 'Max message characters',
                  }),
              },
            ]}>
            <InputNumber
              min={MIN_CHARS}
              max={HARD_MAX_CHARS}
              step={100}
              precision={0}
              placeholder="e.g. 1000"
              className={styles.salesAgentInput}
              parser={value => (value === '' ? '' : parseInt(value, 10))}
            />
          </Form.Item>

          <Form.Item
            name="maxActiveAnonymousTickets"
            label="Max active anonymous tickets per email"
            extra="Concurrent active tickets allowed for one contact email opened without a session. At the cap, a repeat ticket for that email resumes the most-recent one instead of opening a new one. Default: 1."
            rules={[
              {required: true, message: 'Please enter a value'},
              {
                validator: (_, value) =>
                  validateInt(value, {
                    min: MIN_ANON_TICKETS,
                    max: HARD_MAX_ANON_TICKETS,
                    label: 'Max active anonymous tickets',
                  }),
              },
            ]}>
            <InputNumber
              min={MIN_ANON_TICKETS}
              max={HARD_MAX_ANON_TICKETS}
              step={1}
              precision={0}
              placeholder="1"
              className={styles.salesAgentInput}
              parser={value => (value === '' ? '' : parseInt(value, 10))}
            />
          </Form.Item>

          <Form.Item
            name="abandonAfterMinutes"
            label="Abandon idle tickets after (minutes)"
            extra={`Idle tickets (anonymous and authenticated) past this many minutes are swept to ABANDONED. Between ${MIN_ABANDON_MINUTES} and ${HARD_MAX_ABANDON_MINUTES}. Default: ${DEFAULT_ABANDON_MINUTES} (24h).`}
            rules={[
              {required: true, message: 'Please enter a value'},
              {
                validator: (_, value) =>
                  validateInt(value, {
                    min: MIN_ABANDON_MINUTES,
                    max: HARD_MAX_ABANDON_MINUTES,
                    label: 'Abandon-after minutes',
                  }),
              },
            ]}>
            <InputNumber
              min={MIN_ABANDON_MINUTES}
              max={HARD_MAX_ABANDON_MINUTES}
              step={30}
              precision={0}
              placeholder="1440"
              className={styles.salesAgentInput}
              parser={value => (value === '' ? '' : parseInt(value, 10))}
            />
          </Form.Item>

          <SubmitButton disabled={!tabOperations?.update} label="Update" />
        </Form>
      </div>
      <ConfirmModal
        openModal={confirmModalOpen}
        setOpenModal={open => {
          setConfirmModalOpen(open);
          if (!open) setPendingValues(null);
        }}
        loading={loading}
        title={anySoftCapBreach ? 'Unusual value — confirm?' : 'Confirm Update'}
        confirmText={buildConfirmText({
          pendingValues,
          storedTickets,
          storedChars,
          storedAnonTickets,
          storedAbandon,
          exceedsTicketsSoftCap,
          exceedsCharsSoftCap,
          exceedsAnonTicketsSoftCap,
          exceedsAbandonSoftCap,
        })}
        handleOperation={persist}
      />
    </div>
  );
};

// Compose the modal copy so it lists only the fields the operator actually
// changed — saves the agent from re-reading values that aren't moving.
function buildConfirmText({
  pendingValues,
  storedTickets,
  storedChars,
  storedAnonTickets,
  storedAbandon,
  exceedsTicketsSoftCap,
  exceedsCharsSoftCap,
  exceedsAnonTicketsSoftCap,
  exceedsAbandonSoftCap,
}) {
  if (!pendingValues) return '';
  const lines = [];
  if (pendingValues.tickets !== storedTickets) {
    lines.push(
      `• Max active tickets per user: ${storedTickets} → ${pendingValues.tickets}`,
    );
  }
  if ((pendingValues.chars ?? null) !== (storedChars ?? null)) {
    const before = storedChars ?? 'server default';
    const after = pendingValues.chars ?? 'server default';
    lines.push(`• Max characters per user message: ${before} → ${after}`);
  }
  if (pendingValues.anonTickets !== storedAnonTickets) {
    lines.push(
      `• Max active anonymous tickets per email: ${storedAnonTickets} → ${pendingValues.anonTickets}`,
    );
  }
  if (pendingValues.abandonMinutes !== storedAbandon) {
    lines.push(
      `• Abandon idle tickets after: ${storedAbandon} → ${pendingValues.abandonMinutes} minutes`,
    );
  }
  if (lines.length === 0) {
    return 'No changes to apply.';
  }
  const softWarnings = [];
  if (exceedsTicketsSoftCap) {
    softWarnings.push(
      `Max active tickets above ${SOFT_MAX_TICKETS} is almost always a misconfiguration.`,
    );
  }
  if (exceedsCharsSoftCap) {
    softWarnings.push(
      `Max characters above ${SOFT_MAX_CHARS} is unusually large.`,
    );
  }
  if (exceedsAnonTicketsSoftCap) {
    softWarnings.push(
      `Max active anonymous tickets above ${SOFT_MAX_ANON_TICKETS} is unusual and may invite abuse.`,
    );
  }
  if (exceedsAbandonSoftCap) {
    softWarnings.push(
      `Abandoning only after ${SOFT_MAX_ABANDON_MINUTES} minutes (7 days) is unusually long.`,
    );
  }
  const warning = softWarnings.length
    ? `${softWarnings.join(' ')} Continue?`
    : 'Changes propagate to sassandra within ~60 seconds.';
  return `${lines.join('\n')}\n\n${warning}`;
}
